<template>
  <div class="dish-list">
    <DishListSelector @list-change="handleListChange" />
    <div class="top-bar">
      <div class="search-input-wrapper">
        <input
          type="text"
          placeholder="Search dishes..."
          v-model="searchQuery"
          class="search-input"
          maxlength="50"
        />
        <button class="button primary add_dish_button" @click="toggleAddDishForm">
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
      </div>
    </div>

    <div v-show="loading" class="loader"></div>

    <template v-if="filteredFoodList.length > 0">
      <DishListItem
        v-for="(food, index) in filteredFoodList"
        :key="food.id"
        :id="food.id"
        :title="food.title"
        :progress="food.logs?.length || 0"
        :goal="food.goal"
        :foodId="food.id"
        :logs="food.logs || []"
        :expanded="expandedItemIndex === index"
        @toggle="toggleExpand(index)"
        @deleted="handleFoodDeleted"
        @log-deleted="handleLogDeleted"
      />
    </template>

    <template v-else-if="foodList && filteredFoodList.length === 0">
      <h1 class="empty_list_message">No dishes found</h1>
    </template>

    <div v-if="!foodList && fetchError" class="error-message">
      <p>{{ fetchError }}</p>
      <button @click="fetchDishItems(list)">Retry</button>
    </div>

    <AddDishForm @dishAdded="dishAdded" @hideForm="hideForm" :show="show" />
  </div>
</template>

<script setup lang="ts">
// Vue core
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useModal } from 'vue-final-modal';

// Components
import AddDishForm from '@/components/AddDishForm.vue';
import DishListItem from '@/components/DishListItem.vue';
import DishListSelector from './DishListSelector.vue';

// Utils & Types
import { fetchWithAuth } from '@/utils/api';
import { type Dish } from '@/types';

const store = useStore();
const apiUrl = store.getters.getApiUrl;

const show = ref(false);
const foodList = ref<Dish[] | null>(null);
const expandedItemIndex = ref<number | null>(null);
const searchQuery = ref('');
const list = ref<string>('current');
const fetchError = ref<string | null>(null);
const loading = ref(false);

function handleListChange(list: string) {
  foodList.value = null; // Reset food list when changing the list
  fetchDishItems(list);
}

const toggleAddDishForm = () => {
  show.value = true;
};

const hideForm = () => {
  show.value = false;
};

const dishAdded = (new_dish: Dish) => {
  foodList.value?.push({
    ...new_dish,
    logs: new_dish.logs ?? [],
  });
  show.value = false;
};

const toggleExpand = (index: number) => {
  expandedItemIndex.value = expandedItemIndex.value === index ? null : index;
};

const handleLogDeleted = (foodId: number, deletedLogId: number) => {
  foodList.value = foodList.value?.map((food) =>
    food.id === foodId
      ? { ...food, logs: food.logs?.filter((log) => log.id !== deletedLogId) ?? [] }
      : food
  ) || [];
};

const handleFoodDeleted = (deletedFoodId: number) => {
  foodList.value = foodList.value?.filter((food) => food.id !== deletedFoodId) || [];
};

const fetchDishItems = async (list: string) => {
  try {

    // Reset error state
    fetchError.value = null;
    loading.value = true;

    const response = await fetchWithAuth(`${apiUrl}/api/dishes/list/${encodeURIComponent(list)}`, { method: 'GET' }, store);
    if (response.ok) {
      const data = await response.json();
      foodList.value = data.map((item: Dish) => ({
        ...item,
        logs: item.logs ?? [],
      }));
    } else {
      const errorText = await response.text();
      fetchError.value = `Oh no! Something went wrong while loading dishes. Please try again later.`;
    }
    loading.value = false;
  } catch (error) {
    fetchError.value = 'Network error while loading dishes.';
    loading.value = false;
    console.error('Error fetching food items:', error);
  }
};

onMounted(() => fetchDishItems(list.value));

const filteredFoodList = computed(() =>
  foodList.value?.filter(food =>
    food.title.toLowerCase().includes(searchQuery.value.toLowerCase())
  ) ?? []
);

</script>

<style scoped>
.dish-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0 auto;
}

.empty_list_message {
  color: hsl(202, 15%, 80%);
  text-align: center;
  font-size: 1.2rem;
}

.top-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.search-input-wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 5px;
}

.search-icon {
  position: absolute;
  left: 50px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 16px;
  border: 1px solid #ccc;
  border-radius: 5px;
  border: none;
}

.add_dish_button {
  height: 100%;
  width: 47px;
  border-radius: 5px;
}

.dish-list .loader {
  margin-top: 20px;
}

</style>
